'use client';
import { useTranslations } from 'next-intl';
import type { FC, ReactNode } from 'react';
import { Switch } from '@/app/components/ui/switch';
import { useStore } from '@/app/providers/store';
import type { BackfillTaskType } from '@/app/types';
import { cn } from '@/app/utils';

type Option = { label: string; key: BackfillTaskType };

interface BackfillTargetOptionProps {
  upGradeTip?: ReactNode;
  onApply: (taskType: BackfillTaskType) => void;
  selectedType: BackfillTaskType;
  options: Option[];
  showKeepPromptToggle?: boolean;
  keepPrompt?: boolean;
  onKeepPromptChange?: (keepPrompt: boolean) => void;
  titleText?: string;
}

export const BackfillTargetOption: FC<BackfillTargetOptionProps> = ({
  upGradeTip,
  selectedType,
  options,
  onApply,
  showKeepPromptToggle = false,
  keepPrompt = true,
  onKeepPromptChange,
  titleText,
}) => {
  const t = useTranslations();
  const isFree = useStore((state) => state.userInfo?.subs_plan === 'free');

  const renderOption = (option: Option) => {
    const isSelected = selectedType === option.key;

    return (
      <div
        key={option.key}
        className={cn(
          'flex cursor-pointer items-center rounded-4 px-3 py-1.5 hover:bg-system-hover01',
          isSelected && 'bg-system-hover01',
        )}
        onClick={() => onApply(option.key)}
      >
        <div className="mr-2 flex size-4 shrink-0 items-center justify-center rounded-full border border-system-white64">
          {isSelected && <div className="size-2 rounded-full bg-current" />}
        </div>
        <span className="whitespace-nowrap">{option.label}</span>
      </div>
    );
  };

  return (
    <>
      {titleText && <div className="mb-2 text-system-white80">{titleText}</div>}
      {options.map(renderOption)}
      {showKeepPromptToggle && (
        <div className="mt-2 border-system-white32 border-t pt-2">
          <div
            className="flex cursor-pointer items-center justify-between gap-4 rounded-4 py-1.5"
            onClick={() => onKeepPromptChange?.(!keepPrompt)}
          >
            <span className="whitespace-nowrap text-system-white80">{t('frameShotDialog.keepPrompt')}</span>
            <Switch
              className="h-6 w-11 p-0.5"
              checked={keepPrompt}
              onCheckedChange={onKeepPromptChange}
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        </div>
      )}
      {isFree && upGradeTip && <div className="border-system-white32 border-t pt-2">{upGradeTip}</div>}
    </>
  );
};
